// Admin → Customers: every A2000 customer and Deposco trading partner (`routing-customers`, and the
// open lines'), with its open lines and the name every page shows for it (`customer-names.ts`).
// One is typed in place; many at once from the bar that ticking shows — several customers, of
// either source, may share one name. None saved is the source's own rule (A2000 its code, Deposco
// its name), shown greyed; Reset to default goes back to it. Each save is a change of its own in the
// System log (restorable there); the server refuses anyone but the Admin feature. The platform's
// CRUD has no batch: Apply saves one customer after another. Customers sharing a shown name share
// one routing window: a save that changes a window asks first (`rule-check-dialog.tsx`), and saves
// the routing rules it chose before the names.

import { useRef, useState } from "react";
import type { MouseEvent, ReactNode } from "react";
import { RotateCcwIcon } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CUSTOMER_NAMES,
  DEFAULT_CUSTOMER_NAME_FILTERS,
  ROUTING_API,
  SHOWN_NAME_MAX,
  checkShownName,
  countText,
  customerNameText,
  filterCustomerRows,
  formatDay,
  customerKey,
  groupByShown,
  hasRule,
  renameImpact,
  ruleText,
  usedShownNames,
} from "@app/shared";
import type { CustomerNameFilters, CustomerNameRow, CustomerNamesPayload } from "@app/shared";
import {
  Button,
  Checkbox,
  DataError,
  Input,
  TableSkeleton,
  apiGet,
  crudApi,
  crudErrorText,
  useMe,
} from "@axny/platform-ui";
import { cn } from "@/lib/utils";
import { useLoggedWrites } from "./logged-write";
import { notify, useMessage } from "./message";
import { SourceBadge, SourcePicker } from "./source-filter";
import { SearchBox } from "./toolbar";
import { useAdminAskTab } from "./axai-admin";
import { SelectionBar } from "./selection-bar";
import { useRuleCheck } from "./rule-check-dialog";
import { useRuleWrites } from "./routing-rule-writes";
import { editGate } from "./edit-gate";

export const CUSTOMER_NAMES_KEY = ["routing", "admin", "customers"] as const;
/** Ticking every customer the filters show asks first above this many. */
const ASK_ABOVE = 200;

/** One customer's save: its shown name ("" is the default), with its System log entry. */
function useSaveName() {
  const writes = useLoggedWrites();
  const me = useMe();
  const who = me.data?.authenticated ? me.data.user.displayName || me.data.user.username : "";
  return async (row: CustomerNameRow, shownName: string, note: string) => {
    const log = {
      action: "edit" as const,
      lineText: customerNameText(row),
      changes: [{ f: "shownName", from: row.record?.shownName ?? "", to: shownName }],
      note,
    };
    const input = { source: row.source, code: row.code, customerName: row.name, shownName, updatedBy: who };
    if (row.record) {
      // Its version now: another manager may have saved it since.
      const now = await crudApi.get(CUSTOMER_NAMES.id, row.record.id);
      await writes.update(CUSTOMER_NAMES, row.record.id, String(now.rv), input, log);
    } else await writes.create(CUSTOMER_NAMES, input, log);
  };
}

/** What the routing check did before the names: rules saved, and those that failed (cancelled: null). */
export type RulesFirst = { rules: number; failed: string[] } | null;

/**
 * Before shown names are saved: their effect on the routing windows (`renameImpact`); when one
 * changes, the person chooses (`useRuleCheck`), and the routing rules chosen are saved first, one
 * code after another. Null: cancelled — nothing saved. Every way a shown name changes runs it: the
 * cell, the bar, Reset, and a System log Restore (`system-log-page.tsx`).
 */
export function useRulesFirst() {
  const check = useRuleCheck();
  const writes = useRuleWrites();
  const run = async (
    payload: CustomerNamesPayload | undefined,
    changes: readonly { key: string; shownName: string }[],
  ): Promise<RulesFirst> => {
    if (!payload) return { rules: 0, failed: [] };
    const customerNames = payload.customers.flatMap((r) => (r.record ? [r.record] : []));
    const input = { ...payload, customerNames, base: payload.ruleBase };
    const impact = renameImpact({ ...input, changes });
    if (impact.dialog === "none") return { rules: 0, failed: [] };
    const choice = await check.ask(impact);
    if (!choice) return null;
    if (choice.kind === "names") return { rules: 0, failed: [] };
    const recordOf = (key: string) =>
      payload.routingRules.find((r) => customerKey(r.source === "Deposco" ? "Deposco" : "A2000", r.code) === key) ??
      null;
    // "Set the routing window…": every merged code of the group; "Keep their own": each renamed code
    // kept separate (its window as now); "Keep the group's window": each code whose window changes keeps it.
    const plan =
      choice.kind === "set"
        ? impact.joined!.members.map((m) => ({
            m,
            values: { ...choice.rule, noRule: choice.noRule, separate: false },
            notes: choice.notes,
            note: "Set with the shown name",
          }))
        : choice.kind === "own"
          ? impact.keepOwn.map((m) => ({
              m,
              values: m.values,
              notes: String(recordOf(m.key)?.notes ?? ""),
              note: "Kept its own routing window when its shown name changed",
            }))
          : impact.leaving.map((m) => ({
              m,
              values: {
                openBdays: m.before.openBdays,
                dueBdays: m.before.dueBdays,
                noRule: !hasRule(m.before),
                separate: false,
              },
              notes: "",
              note: `Kept the group's window (${ruleText(m.before)}) when its shown name changed`,
            }));
    let rules = 0;
    const failed: string[] = [];
    for (const p of plan) {
      const r = await writes.each(
        [{ source: p.m.source, code: p.m.code, name: p.m.name, record: recordOf(p.m.key) }],
        (t) => writes.save(t, p.values, p.notes, p.note),
      );
      rules += r.saved;
      for (const x of r.failed)
        failed.push(`Routing rule ${x.target.source} ${x.target.code} · ${x.target.name} — ${x.why}`);
    }
    return { rules, failed };
  };
  return { run, dialog: check.dialog };
}

export function CustomerNamesTab() {
  const message = useMessage();
  const queryClient = useQueryClient();
  const save = useSaveName();
  const [filters, setFilters] = useState<CustomerNameFilters>(DEFAULT_CUSTOMER_NAME_FILTERS);
  const [grouped, setGrouped] = useState(false);
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());
  /** The last row ticked: Shift+click ticks the range from it. */
  const [anchor, setAnchor] = useState<string | null>(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const data = useQuery({
    queryKey: CUSTOMER_NAMES_KEY,
    queryFn: ({ signal }) => apiGet<CustomerNamesPayload>(`${ROUTING_API}/admin/customers`, signal),
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
  const rows = data.data?.customers ?? [];
  const shown = filterCustomerRows(rows, filters);
  const used = usedShownNames(rows.flatMap((r) => (r.record ? [r.record] : [])));
  const set = (next: Partial<CustomerNameFilters>) => setFilters((f) => ({ ...f, ...next }));
  // Ask AI sets the search and the filters (`axai-admin.ts`).
  useAdminAskTab({
    view: { tab: "customers", filters },
    apply: (v) => v.tab === "customers" && setFilters(v.filters),
    shownFor: (v) => (v.tab === "customers" ? filterCustomerRows(rows, v.filters).length : 0),
  });
  const applying = useRef(false);
  const check = useRulesFirst();
  const rulesFirst = {
    dialog: check.dialog,
    run: (changes: readonly { key: string; shownName: string }[]) => check.run(data.data, changes),
  };

  if (data.isPending) return <TableSkeleton />;
  if (data.isError) return <DataError error={data.error} />;

  const order = grouped ? groupByShown(shown).flatMap((g) => g.rows) : shown;
  const tick = (row: CustomerNameRow, on: boolean, e?: MouseEvent) => {
    const keys =
      e?.shiftKey && anchor
        ? (() => {
            const ids = order.map((r) => r.key);
            const from = ids.indexOf(anchor);
            const to = ids.indexOf(row.key);
            return from < 0 ? [row.key] : ids.slice(Math.min(from, to), Math.max(from, to) + 1);
          })()
        : [row.key];
    setTicked((t) => {
      const next = new Set(t);
      for (const k of keys) {
        if (on) next.add(k);
        else next.delete(k);
      }
      return next;
    });
    setAnchor(row.key);
  };
  const allTicked = shown.length > 0 && shown.every((r) => ticked.has(r.key));
  const tickAll = async (on: boolean) => {
    if (!on) return setTicked(new Set());
    if (
      shown.length > ASK_ABOVE &&
      !(await message.confirm(
        `Tick ${shown.length.toLocaleString("en-US")} customers?`,
        "Every customer the filters show is ticked. Narrow the filters first to tick fewer.",
        { confirmLabel: `Tick ${shown.length.toLocaleString("en-US")}` },
      ))
    )
      return;
    setTicked(new Set(shown.map((r) => r.key)));
  };

  /** Saves `name` on every ticked customer, one after another; only the failed ones stay ticked. */
  const apply = async (name: string, note: string) => {
    const of = rows.filter((r) => ticked.has(r.key));
    if (!of.length) return;
    // The routing windows first: asked, and the rules chosen saved; Cancel saves nothing, the selection stays.
    const first = await rulesFirst.run(of.map((r) => ({ key: r.key, shownName: name })));
    if (!first) return;
    setBusy(true);
    const failed: { row: CustomerNameRow; why: string }[] = [];
    let saved = 0;
    try {
      for (const r of of) {
        if ((r.record?.shownName ?? "").trim() === name) {
          saved++;
          continue;
        }
        try {
          await save(r, name, note);
          saved++;
        } catch (e) {
          failed.push({ row: r, why: crudErrorText(e) });
        }
      }
    } finally {
      setBusy(false);
      setTicked(new Set(failed.map((f) => f.row.key)));
      await queryClient.invalidateQueries({ queryKey: ["routing"] });
    }
    const rules = first.rules ? ` · ${countText(first.rules, "routing rule")}` : "";
    if (failed.length || first.failed.length)
      await message.error(
        `Saved ${countText(saved, "name")}${rules} · ${failed.length + first.failed.length} failed`,
        failed.length && first.rules
          ? "The routing rules are saved and apply once the names are: the failed names stay ticked — Apply again."
          : "The failed ones stay ticked: Apply again, or check them one by one.",
        [...first.failed, ...failed.map((f) => `${f.row.source} ${f.row.code} · ${f.row.name} — ${f.why}`)],
      );
    else notify(`Saved ${countText(saved, "name")}${rules}`, "Every page shows the new names on its next refresh.");
    if (!failed.length) setTyped("");
  };

  const applyTyped = async () => {
    // Enter and the button, or Enter twice: one Apply at a time.
    if (applying.current) return;
    applying.current = true;
    try {
      await applyChecked();
    } finally {
      applying.current = false;
    }
  };
  const applyChecked = async () => {
    const check = checkShownName(typed, used);
    if (!check.ok) return void (await message.error("Check the shown name", check.message));
    if (
      check.warning &&
      !(await message.confirm(check.warning.title, check.warning.text, {
        lines: check.warning.lines,
        confirmLabel: "Use it anyway",
      }))
    )
      return;
    await apply(check.value, `Set on ${countText(ticked.size, "customer")} at once`);
  };

  return (
    <div className="space-y-3">
      {rulesFirst.dialog}
      <div className="flex flex-wrap items-center gap-2">
        <SourcePicker
          value={filters.source}
          counts={{
            A2000: rows.filter((r) => r.source === "A2000").length,
            Deposco: rows.filter((r) => r.source === "Deposco").length,
          }}
          onChange={(source) => set({ source })}
        />
        <SearchBox
          value={filters.search}
          onChange={(search) => set({ search })}
          placeholder="Search customers — name, code, shown name"
        />
        <label className="flex items-center gap-1.5 text-xs">
          <Checkbox checked={filters.activeOnly} onCheckedChange={(c) => set({ activeOnly: !!c })} />
          Active only
        </label>
        <label className="flex items-center gap-1.5 text-xs">
          <Checkbox checked={filters.withOpenLines} onCheckedChange={(c) => set({ withOpenLines: !!c })} />
          With open lines
        </label>
        <select
          className="h-7 rounded-md border bg-transparent px-2 text-xs"
          aria-label="Named or not"
          value={filters.named}
          onChange={(e) => set({ named: e.target.value as CustomerNameFilters["named"] })}
        >
          <option value="all">Named and not named</option>
          <option value="named">Named</option>
          <option value="default">Not named</option>
        </select>
        <label className="flex items-center gap-1.5 text-xs">
          <Checkbox checked={grouped} onCheckedChange={(c) => setGrouped(!!c)} />
          Group by shown name
        </label>
        <p className="text-muted-foreground ml-auto text-xs" data-testid="customer-names-count">
          {countText(shown.length, "customer")} · {countText(shown.filter((r) => !r.isDefault).length, "shown name")} of
          their own
        </p>
      </div>

      <SelectionBar
        active={ticked.size > 0}
        hint="Tick customers to name several at once — Shift+click ticks a range"
        data-testid="customer-names-bar"
      >
        <span className="font-medium">{ticked.size.toLocaleString("en-US")} selected</span>
        <span className="text-muted-foreground">·</span>
        <span>Shown as</span>
        <Input
          className="h-7 w-56 text-[13px]"
          aria-label="Shown name for the selected customers"
          list="customer-shown-names"
          maxLength={SHOWN_NAME_MAX + 8}
          disabled={busy}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void applyTyped();
            }
          }}
        />
        <datalist id="customer-shown-names">
          {used.map((u) => (
            <option key={u} value={u} />
          ))}
        </datalist>
        <Button size="sm" disabled={busy || !typed.trim()} onClick={() => void applyTyped()}>
          Apply
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="gap-1"
          disabled={busy}
          onClick={() => void apply("", "Reset to default")}
        >
          <RotateCcwIcon className="size-3.5" />
          Reset to default
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => setTicked(new Set())}>
          Clear selection
        </Button>
        {busy ? <span className="text-muted-foreground text-xs">Saving…</span> : null}
      </SelectionBar>

      <div className="overflow-x-auto rounded-md border" data-testid="admin-customers">
        <table className="w-full min-w-[900px] text-[13px]">
          <thead className="bg-locked text-left text-xs">
            <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:font-medium">
              <th className="w-10">
                <Checkbox
                  aria-label="Tick every customer the filters show"
                  checked={allTicked}
                  onCheckedChange={(c) => void tickAll(!!c)}
                />
              </th>
              <th className="w-24">Source</th>
              <th className="w-36">Code</th>
              <th>Name</th>
              <th className="w-64">Shown as</th>
              <th className="w-16">Active</th>
              <th className="w-24 text-right">Open lines</th>
              <th className="w-48" />
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-muted-foreground p-6">
                  {rows.length ? "No customer matches the filters." : "No customer in the data yet."}
                </td>
              </tr>
            ) : null}
            {grouped
              ? groupByShown(shown).map((g) => (
                  <GroupRows key={g.shown} shown={g.shown} rows={g.rows}>
                    {g.rows.map((r) => (
                      <CustomerLine
                        key={r.key}
                        row={r}
                        used={used}
                        ticked={ticked.has(r.key)}
                        onTick={tick}
                        save={save}
                        rulesFirst={rulesFirst.run}
                      />
                    ))}
                  </GroupRows>
                ))
              : shown.map((r) => (
                  <CustomerLine
                    key={r.key}
                    row={r}
                    used={used}
                    ticked={ticked.has(r.key)}
                    onTick={tick}
                    save={save}
                    rulesFirst={rulesFirst.run}
                  />
                ))}
          </tbody>
        </table>
      </div>
      <footer className="text-muted-foreground flex flex-wrap gap-x-4 text-xs">
        <span>
          A2000's customer master and Deposco's trading partners · by default A2000 shows its code, Deposco its name ·
          at most {SHOWN_NAME_MAX} characters · on the next refresh of each page
        </span>
        <span className="ml-auto">
          Display only: A2000 and Deposco data and saved records keep the original name; My customers go by code
        </span>
      </footer>
    </div>
  );
}

/** "Group by shown name": the name once, what it covers under it. */
function GroupRows({
  shown,
  rows,
  children,
}: {
  shown: string;
  rows: readonly CustomerNameRow[];
  children: ReactNode;
}) {
  const sources = [...new Set(rows.map((r) => r.source))].join(" + ");
  return (
    <>
      <tr className="bg-muted/40 border-t">
        <td colSpan={8} className="px-3 py-1.5 text-xs">
          <span className="text-[13px] font-semibold">{shown}</span>
          <span className="text-muted-foreground">
            {" "}
            · {countText(rows.length, "customer")} · {sources} ·{" "}
            {countText(
              rows.reduce((n, r) => n + r.openLines, 0),
              "open line",
            )}
          </span>
        </td>
      </tr>
      {children}
    </>
  );
}

/** One customer: its shown name typed in place (Enter or leaving the cell saves; Esc puts it back). */
function CustomerLine({
  row,
  used,
  ticked,
  onTick,
  save,
  rulesFirst,
}: {
  row: CustomerNameRow;
  used: readonly string[];
  ticked: boolean;
  onTick: (row: CustomerNameRow, on: boolean, e?: MouseEvent) => void;
  save: (row: CustomerNameRow, shownName: string, note: string) => Promise<void>;
  rulesFirst: (changes: readonly { key: string; shownName: string }[]) => Promise<RulesFirst>;
}) {
  const message = useMessage();
  const queryClient = useQueryClient();
  const [typed, setTypedState] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // One typed edit, one save (`edit-gate.ts`): Enter leaves the cell, and only that blur saves.
  const gate = useRef(editGate());
  const setTyped = (next: string | null) => {
    if (next === null) gate.current.cancel();
    else gate.current.edit();
    setTypedState(next);
  };
  // The default is the box's placeholder, never its text: typing replaces it, never adds to it.
  const value = typed ?? (row.isDefault ? "" : row.shown);

  const run = async (shownName: string, note: string) => {
    // The routing windows first: asked, and the rules chosen saved; Cancel saves nothing.
    const first = await rulesFirst([{ key: row.key, shownName }]);
    if (!first) return setTyped(null);
    if (first.failed.length)
      await message.error("Some routing rules weren't saved", "The shown name is saved next.", first.failed);
    setBusy(true);
    try {
      await save(row, shownName, note);
      setTyped(null);
      notify(
        shownName ? `${row.code}: ${shownName}` : `${row.code}: back to the default`,
        first.rules
          ? `And ${countText(first.rules, "routing rule")}. Every page shows it on its next refresh.`
          : "Every page shows it on its next refresh.",
      );
    } catch (e) {
      await message.error("Couldn't save the shown name", crudErrorText(e), [
        ...(first.rules
          ? [`The ${countText(first.rules, "routing rule")} saved stay, and apply once the name is saved.`]
          : []),
        "Someone may have saved it at the same time: the list is read again.",
      ]);
      setTyped(null);
    } finally {
      setBusy(false);
      await queryClient.invalidateQueries({ queryKey: ["routing"] });
    }
  };

  const commit = async () => {
    if (typed === null || !gate.current.take()) return;
    if (typed.trim() === row.shown) return setTyped(null);
    // Emptied: back to the default, as Reset.
    if (!typed.trim()) return row.isDefault ? setTyped(null) : run("", `Reset to default (${row.defaultShown})`);
    const check = checkShownName(typed, used);
    if (!check.ok) {
      gate.current.edit();
      return void (await message.error("Check the shown name", check.message));
    }
    if (check.value === row.shown) return setTyped(null);
    if (
      check.warning &&
      !(await message.confirm(check.warning.title, check.warning.text, {
        lines: check.warning.lines,
        confirmLabel: "Use it anyway",
      }))
    )
      return setTyped(null);
    await run(check.value, check.value === row.defaultShown ? "The same as the default" : "");
  };

  const saved = row.record && !row.isDefault ? row.record : null;
  return (
    <tr
      className={cn("border-t align-middle [&>td]:px-3 [&>td]:py-1", busy && "opacity-60", ticked && "bg-primary/5")}
      data-testid="customer-name-row"
    >
      <td>
        <input
          type="checkbox"
          aria-label={`Select ${row.code} ${row.name}`}
          checked={ticked}
          onClick={(e) => onTick(row, e.currentTarget.checked, e)}
          readOnly
        />
      </td>
      <td>
        <SourceBadge source={row.source} />
      </td>
      <td className="font-mono text-xs">{row.code}</td>
      <td className="truncate" title={`${row.name} · ${row.code} · ${row.source}`}>
        {row.name || <span className="text-muted-foreground">—</span>}
      </td>
      <td>
        <Input
          className="h-7 w-60 text-[13px]"
          aria-label={`Shown name of ${row.code}`}
          maxLength={SHOWN_NAME_MAX + 8}
          disabled={busy}
          placeholder={row.defaultShown}
          value={value}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => {
            // Enter saves by leaving the cell: one save, never two. Its default is cancelled: the
            // same key would otherwise press the button of the dialog the save opens.
            if (e.key === "Enter") {
              e.preventDefault();
              e.currentTarget.blur();
            }
            if (e.key === "Escape") setTyped(null);
            if (e.key === "Delete" && !row.isDefault && typed === null) {
              e.preventDefault();
              void run("", `Reset to default (${row.defaultShown})`);
            }
          }}
          onBlur={() => void commit()}
        />
      </td>
      <td className="text-xs">{row.active ? "Yes" : <span className="text-muted-foreground">No</span>}</td>
      <td className="text-right tabular-nums">{row.openLines.toLocaleString("en-US")}</td>
      <td>
        <div className="flex items-center gap-2">
          {saved ? (
            <span className="text-muted-foreground text-xs">
              {saved.updatedBy ? `${saved.updatedBy}` : "Saved"}
              {saved.updated_at ? ` · ${formatDay(saved.updated_at.slice(0, 10))}` : ""}
            </span>
          ) : (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">Default</span>
          )}
          {saved ? (
            <Button
              size="xs"
              variant="outline"
              className="ml-auto gap-1"
              disabled={busy}
              title={`Back to ${row.defaultShown}, the source's own`}
              onClick={() => void run("", `Reset to default (${row.defaultShown})`)}
            >
              <RotateCcwIcon className="size-3.5" />
              Reset
            </Button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}
